import React, { useState, useEffect } from 'react';
import { Button, Card, List, Typography, Space, Tag, Dropdown, Menu, Modal, message } from 'antd';
import { 
  PlusOutlined, 
  DatabaseOutlined, 
  FileExcelOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ExperimentOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import DataPreviewModal from './DataPreviewModal';
import DataSourceUpload from './DataSourceUpload';
import CloverConnectionSetup from './CloverConnectionSetup';
import POSConnectionSetup from './POSConnectionSetup';
import ConnectionTypeSelector from './ConnectionTypeSelector';
import axios from 'axios';

const { Title, Text } = Typography;

const ConnectionsTab = () => {
  // Helper function to handle authentication errors
  const handleAuthError = (error) => {
    if (error.response?.status === 401 || error.response?.status === 204 || 
        (error.response?.status === 400)) {
      localStorage.removeItem('auth-token');
      message.error('Session expired. Please log in again.');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const [connections, setConnections] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCloverModalOpen, setIsCloverModalOpen] = useState(false);
  const [isPOSModalOpen, setIsPOSModalOpen] = useState(false);
  const [selectedPOSProvider, setSelectedPOSProvider] = useState('clover');
  const [isConnectionTypeSelectorOpen, setIsConnectionTypeSelectorOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [editingSchema, setEditingSchema] = useState(null);

  // Load connections from API
  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await axios.post('/workflow/connections', {transformer: false}, {
        headers: {
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      if (response.data.success) {
        setConnections(response.data.data);
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error fetching connections:', error);
      message.error('Failed to load connections');
    }
  };

  const handleCreateConnection = () => {
    setIsConnectionTypeSelectorOpen(true);
  };

  const handleConnectionTypeSelect = (type) => {
    if (type === 'spreadsheet') {
      setEditingConnection(null);
      setIsUploadModalOpen(true);
    } else if (type === 'clover') {
      setIsCloverModalOpen(true);
    } else if (type === 'square') {
      setSelectedPOSProvider('square');
      setIsPOSModalOpen(true);
    }
  };

  const handleCreateCloverConnection = () => {
    setIsCloverModalOpen(true);
  };

  const handleCreatePOSConnection = (provider = 'clover') => {
    setSelectedPOSProvider(provider);
    setIsPOSModalOpen(true);
  };

  const handleCloverConnectionComplete = () => {
    // Refresh connections list and close modal
    fetchConnections();
    setIsCloverModalOpen(false);
    message.success('Clover connection created successfully!');
  };

  const handlePOSConnectionComplete = () => {
    // Refresh connections list and close modal
    fetchConnections();
    setIsPOSModalOpen(false);
    message.success('POS connection created successfully!');
  };

  const handleEditConnection = (connection) => {
    setEditingConnection(connection);
    setEditingSchema(null);
    setIsUploadModalOpen(true);
  };

  const handleEditSchema = (connection) => {
    setEditingSchema(connection);
    setEditingConnection(null);
    setIsUploadModalOpen(true);
  };

  const handleDeleteConnection = (connectionId) => {
    Modal.confirm({
      title: 'Delete Data Source',
      content: 'Are you sure you want to delete this data source? This action cannot be undone. If this data source is associated with a workflow, it cannot be deleted.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          const response = await axios.post('/workflow/connection-action', {
            action: 'delete',
            connectionId: connectionId
          }, {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': localStorage.getItem('auth-token')
            }
          });
          
          if (response.data.success) {
            setConnections(prev => prev.filter(c => c._id !== connectionId));
            message.success('Data source deleted successfully');
          } else {
            message.error(response.data.error || 'Failed to delete data source');
          }
        } catch (error) {
          if (handleAuthError(error)) return;
          console.error('Error deleting connection:', error);
          message.error('Failed to delete data source');
        }
      },
    });
  };



  const handlePreviewData = async (connection) => {
    try {
      message.loading('Loading sample data...', 2);
      
      // Fetch sample data from the API
      const response = await axios.post('/workflow/sample-data', {
        connectionId: connection.id,
        limit: 1000
      }, {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      if (response.data.success) {
        const sampleData = response.data.data;
        
        // Handle case where multiple sheets/sources are available but none specified
        if (sampleData.availableSheets && sampleData.message) {
          // For Excel files with multiple sheets, show sheet selection
          message.info('Multiple sheets available. Showing first sheet.');
          // Re-fetch with first sheet
          const sheetResponse = await axios.post('/workflow/sample-data', {
            connectionId: connection.id,
            limit: 1000,
            sheetName: sampleData.availableSheets[0].sheetName
          }, {
            headers: {
              'Content-Type': 'application/json',
              'auth-token': localStorage.getItem('auth-token')
            }
          });
          
          if (sheetResponse.data.success) {
            const sheetData = sheetResponse.data.data;
            const previewRows = sheetData.sampleRows.map((row, index) => ({
              ...row.data,
              _rowIndex: row.rowIndex,
              _sampleIndex: index + 1
            }));
            
            const columns = sheetData.columnMappings?.map(col => col.name) || 
                           (previewRows.length > 0 ? Object.keys(previewRows[0]).filter(key => !key.startsWith('_')) : []);
            
            setPreviewData({
              connection,
              data: previewRows,
              columns: columns,
              metadata: {
                fileName: sheetData.fileName,
                sheetName: sheetData.sheetName,
                totalSampleRows: sheetData.totalSampleRows,
                returnedRows: sheetData.returnedRows,
                totalRowsInFile: sheetData.metadata?.totalRowsInFile,
                extractedAt: sheetData.metadata?.extractedAt,
                availableSheets: sampleData.availableSheets
              }
            });
            setIsPreviewOpen(true);
            message.destroy();
            return;
          }
        }
        
        // Handle normal case with sample data
        if (sampleData.sampleRows) {
          // Convert the sample rows to a format suitable for the preview modal
          const previewRows = sampleData.sampleRows.map((row, index) => ({
            ...row.data,
            _rowIndex: row.rowIndex, // Keep original row index
            _sampleIndex: index + 1   // Sequential index for display
          }));
          
          // Get column names from the first row or column mappings
          const columns = sampleData.columnMappings?.map(col => col.name) || 
                         (previewRows.length > 0 ? Object.keys(previewRows[0]).filter(key => !key.startsWith('_')) : []);
          
          setPreviewData({
            connection,
            data: previewRows,
            columns: columns,
            metadata: {
              fileName: sampleData.fileName,
              sheetName: sampleData.sheetName,
              totalSampleRows: sampleData.totalSampleRows,
              returnedRows: sampleData.returnedRows,
              totalRowsInFile: sampleData.metadata?.totalRowsInFile,
              extractedAt: sampleData.metadata?.extractedAt,
              availableSources: sampleData.availableSources // For POS connections
            }
          });
          setIsPreviewOpen(true);
        } else {
          message.error('No sample data available for this connection');
        }
        message.destroy(); // Clear loading message
      } else {
        message.error(response.data.error || 'Failed to load sample data');
      }
    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error fetching sample data:', error);
      message.error('Failed to load sample data. Please try again.');
    }
  };

  const handleUploadComplete = (uploadResult) => {
    // Refresh the connections list to show the new/updated connection
    fetchConnections();
    setIsUploadModalOpen(false);
    setEditingConnection(null);
    message.success(editingConnection ? 'Data source updated successfully!' : 'Data source connection created successfully!');
  };

  const getConnectionTypeInfo = (connection) => {
    if (connection.connectionType === 'api') {
      const apiTypeMap = {
        'clover_pos': { label: 'Clover POS', icon: <DatabaseOutlined />, color: '#52c41a' },
        'square_pos': { label: 'Square POS', icon: <DatabaseOutlined />, color: '#722ed1' },
        'shopify': { label: 'Shopify', icon: <DatabaseOutlined />, color: '#95de64' }
      };
      return apiTypeMap[connection.apiType] || { label: 'API Connection', icon: <DatabaseOutlined />, color: '#1890ff' };
    } else {
      const fileTypeMap = {
        'csv': { label: 'CSV File', icon: <FileExcelOutlined />, color: '#fa8c16' },
        'excel': { label: 'Excel File', icon: <FileExcelOutlined />, color: '#13c2c2' }
      };
      return fileTypeMap[connection.fileType] || { label: 'File Upload', icon: <FileExcelOutlined />, color: '#666' };
    }
  };

  const getConnectionMenu = (connection) => (
    <Menu>
      <Menu.Item 
        key="edit" 
        icon={<EditOutlined />} 
        onClick={() => handleEditConnection(connection)}
      >
        Edit Data Source
      </Menu.Item>
      <Menu.Item 
        key="editSchema" 
        icon={<EditOutlined />} 
        onClick={() => handleEditSchema(connection)}
      >
        Edit Schema
      </Menu.Item>
      <Menu.Item 
        key="preview" 
        icon={<EyeOutlined />} 
        onClick={() => handlePreviewData(connection)}
      >
        Preview Data
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        icon={<DeleteOutlined />} 
        danger
        onClick={() => handleDeleteConnection(connection.id)}
      >
        Delete Connection
      </Menu.Item>
    </Menu>
  );


  return (
    <div className="workflows-container">
      <div className="workflows-header">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Data Source Connections
          </Title>
          <Text type="secondary">
            Manage your data source connections - spreadsheets, POS systems, and APIs
          </Text>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          size="large"
          onClick={handleCreateConnection}
        >
          Create New Connection
        </Button>
      </div>

      <div className="workflows-content">
        {connections.length === 0 ? (
          <div className="empty-state">
            <DatabaseOutlined />
            <Title level={4} type="secondary">
              No data sources configured
            </Title>
            <Text type="secondary" style={{ marginBottom: '24px', display: 'block' }}>
              Create your first data source connection - upload files or connect to POS systems
            </Text>

          </div>
        ) : (
          <div className="workflow-list">
            <List
              grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
              dataSource={connections}
              renderItem={(connection) => {
                const typeInfo = getConnectionTypeInfo(connection);
                return (
                  <List.Item>
                    <Card
                      className="connection-card"
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <Space>
                            {typeInfo.icon}
                            <span>{connection.name}</span>
                          </Space>
                          <Dropdown 
                            overlay={getConnectionMenu(connection)} 
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button type="text" icon={<MoreOutlined />} size="small" />
                          </Dropdown>
                        </div>
                      }
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Text type="secondary">{connection.description}</Text>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <Space>
                          <Tag color={typeInfo.color}>{typeInfo.label}</Tag>
                          <div className={`connection-status ${connection.status}`}>
                            {connection.status === 'ready' ? (
                              <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            ) : connection.status === 'uploaded' ? (
                              <CheckCircleOutlined style={{ color: '#1890ff' }} />
                            ) : (
                              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            )}
                            <span style={{ marginLeft: '4px', textTransform: 'capitalize' }}>
                              {connection.status}
                            </span>
                          </div>
                        </Space>
                      </div>

                      {connection.connectionType === 'api' ? (
                        <>
                          {connection.apiConfig?.merchantId && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Merchant: {connection.apiConfig.merchantId}
                            </div>
                          )}
                          {connection.apiConfig?.environment && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Environment: {connection.apiConfig.environment}
                            </div>
                          )}
                          {connection.apiConfig?.connectedAt && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Connected: {new Date(connection.apiConfig.connectedAt).toLocaleString()}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {connection.fileInfo?.originalName && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              File: {connection.fileInfo.originalName}
                            </div>
                          )}
                          {connection.fileInfo?.fileSize && (
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Size: {(connection.fileInfo.fileSize / 1024 / 1024).toFixed(2)} MB
                            </div>
                          )}
                        </>
                      )}

                      {connection.workflowName && (
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                          <strong>Used by:</strong> {connection.workflowName}
                        </div>
                      )}

                      {connection.createdAt && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Created: {new Date(connection.createdAt).toLocaleString()}
                        </div>
                      )}
                    </Card>
                  </List.Item>
                );
              }}
            />
          </div>
        )}
      </div>

      <Modal
        title={editingSchema ? "Edit Data Schema" : editingConnection ? "Edit Data Source" : "Upload Data Source"}
        open={isUploadModalOpen}
        onCancel={() => {
          setIsUploadModalOpen(false);
          setEditingConnection(null);
          setEditingSchema(null);
        }}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <DataSourceUpload
          onUploadComplete={handleUploadComplete}
          onCancel={() => {
            setIsUploadModalOpen(false);
            setEditingConnection(null);
            setEditingSchema(null);
          }}
          editingConnection={editingConnection}
          editingSchema={editingSchema}
        />
      </Modal>

      <DataPreviewModal
        open={isPreviewOpen}
        data={previewData}
        onClose={() => setIsPreviewOpen(false)}
      />

      <Modal
        title="Connect to Clover POS"
        open={isCloverModalOpen}
        onCancel={() => setIsCloverModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CloverConnectionSetup
          onConnectionComplete={handleCloverConnectionComplete}
          onCancel={() => setIsCloverModalOpen(false)}
        />
      </Modal>

      <Modal
        title={`Connect to ${selectedPOSProvider === 'square' ? 'Square' : 'Clover'} POS`}
        open={isPOSModalOpen}
        onCancel={() => setIsPOSModalOpen(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <POSConnectionSetup
          defaultProvider={selectedPOSProvider}
          onConnectionComplete={handlePOSConnectionComplete}
          onCancel={() => setIsPOSModalOpen(false)}
        />
      </Modal>

      <ConnectionTypeSelector
        open={isConnectionTypeSelectorOpen}
        onClose={() => setIsConnectionTypeSelectorOpen(false)}
        onSelectType={handleConnectionTypeSelect}
      />
    </div>
  );
};

export default ConnectionsTab;